import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import useDebounceState from '@/hooks/useDebounceState';
import useContentCollections from '@/modules/corporate/modules/group/views/GroupDetails/hooks/useContentCollections';
import {
  ContentCollectionGroupType,
  ContentEntity,
  DueDateNestingLevelEnum,
  GroupAdditionMethodEnum,
} from '@/types/schema';

import { RenderRemoveDueDate } from '../../renderer';
import { AdaptedNestedDueDateConfig } from '../ContentAndCollectionList/hooks/useContentAndCollectionList';
import { nestedDialogClass } from '../SetDueDateDialog/styles';
import ContentDetails from './ContentDetail';

interface CollectionContentsProps {
  actionPrimaryText: string;
  title: string;
  description: string;
  collection: ContentCollectionGroupType;
  onCloseDueDateDialog: () => void;
  onSetDueDateForContent: (
    content: ContentEntity,
    dueDateNestingLevel?: DueDateNestingLevelEnum | undefined,
    isNested?: boolean,
  ) => void;
  nestedDueDateConfig: AdaptedNestedDueDateConfig;
  onSetNestedDueDate: () => void;
  onReset: () => void;
}

function CollectionContents(props: CollectionContentsProps) {
  const { t } = useTranslation();
  const {
    actionPrimaryText,
    title,
    description,
    onCloseDueDateDialog,
    collection,
    onSetDueDateForContent,
    nestedDueDateConfig,
    onSetNestedDueDate,
    onReset,
  } = props;
  const [searchInput, setSearchStr, debouncedSearchStr] = useDebounceState('');
  const { contents, isFetching, fetchMore } = useContentCollections({
    filters: {
      groupFilter: { groupIds: [collection.id], groupAdditionMethod: GroupAdditionMethodEnum.All },
      name: debouncedSearchStr,
    },
    groupId: collection.id!,
    pause: !collection.id,
  });

  const renderContent = useCallback(() => {
    if (isFetching) {
      return <Loader />;
    }

    if (!contents?.length) {
      return (
        <EmptyState
          className="flex-1"
          description={t('Common:EmptyState.NoResultsSearchDescription')}
          title={t('Common:EmptyState.NoResultsSearch', { searchText: searchInput })}
        />
      );
    }

    if (contents?.length) {
      return (
        <ContentListModal.Body
          className="flex-1 px-12"
          itemRenderer={(index: number) => {
            const content = contents[index] as ContentEntity;

            return (
              <ContentDetails
                key={content.id}
                content={content}
                dueDateConfig={nestedDueDateConfig?.[content.id]?.dueDateConfig}
                onSetDueDateForContent={onSetDueDateForContent}
              />
            );
          }}
        />
      );
    }

    return null;
  }, [isFetching, contents, t, searchInput, nestedDueDateConfig, onSetDueDateForContent]);

  return (
    <ContentListModal.Main
      action={{
        text: actionPrimaryText,
        onSubmit: onSetNestedDueDate,
        TertiaryAction: RenderRemoveDueDate(t, onReset),
      }}
      classes={nestedDialogClass}
      fetchMore={fetchMore}
      header={{
        title,
        description,
      }}
      items={contents}
      searchBar={{
        value: searchInput,
        onChange: setSearchStr,
        selectedCount: 0,
        placeholder: t('Common:Search'),
      }}
      onClose={onCloseDueDateDialog}>
      {renderContent()}
    </ContentListModal.Main>
  );
}

export default CollectionContents;
